<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useHomeworksStore } from '@/stores/homeworks';
import authApi from "@/api/api.ts";
import { useFullscreen } from '@vueuse/core';

import trainer1 from '@/assets/backgrounds/trainers/1.png';
import trainer2 from '@/assets/backgrounds/trainers/2.png';
import trainer3 from '@/assets/backgrounds/trainers/3.png';
import trainer4 from '@/assets/backgrounds/trainers/4.png';
import trainer5 from '@/assets/backgrounds/trainers/5.png';
import trainer6 from '@/assets/backgrounds/trainers/6.png';
import trainer7 from '@/assets/backgrounds/trainers/7.png';
import trainer8 from '@/assets/backgrounds/trainers/8.png';
import trainer9 from '@/assets/backgrounds/trainers/9.png';
import trainer10 from '@/assets/backgrounds/trainers/10.png';
import trainer11 from '@/assets/backgrounds/trainers/11.png';
import trainer12 from '@/assets/backgrounds/trainers/12.png';
import trainer13 from '@/assets/backgrounds/trainers/13.png';
import trainer14 from '@/assets/backgrounds/trainers/14.png';
import trainer15 from '@/assets/backgrounds/trainers/15.png';
import trainer17 from '@/assets/backgrounds/trainers/16.png';
import trainer16 from '@/assets/backgrounds/trainers/17.png';
import trainer18 from '@/assets/backgrounds/trainers/18.png';

interface Trainer {
  id: number;
  name: string;
  slug: string;
  image: string;
  path: string;
  defaultConfig: Record<string, any>;
  backgroundColor?: string;
  iframeUrl?: string; // Добавлено для соответствия проверке в коде
  coinConditions?: {
    items: string[];
    rules?: {
      maxMistakes?: number;
      requiredWin?: boolean;
      minActions?: number;
      minSteps?: number;
      minFieldSize?: number;
    };
  };
}

const trainers: Trainer[] = [
  {
    id: 1,
    name: 'findPair',
    slug: 'znaidi-paru',
    image: trainer1,
    path: 'memo',
    defaultConfig: { hintTime: 3, cardsAmount: 8, timerDuration: 30, isTimerDisabled: false, isSoundEnabled: true },
    backgroundColor: '#0066FF',
    coinConditions: {
      items: ['games.conditions.zero_mistakes'],
      rules: {
        maxMistakes: 0,
        requiredWin: true
      }
    }
  },
  { id: 2, name: 'readingSpeedTest', slug: 'test-shvydkosti-chytannia', image: trainer2, path: 'reading-speed-test', defaultConfig: { timerDuration: 60, haltOnTimerRunOut: true }, backgroundColor: '#FF7B7B' },
  { id: 3, name: 'schulteTable', slug: 'tablitsia-shulte', image: trainer3, path: 'schulte-table', defaultConfig: { mode: "NUMBERS", size: 3, order: "DIRECT", rotation: 0, shuffleWhenClicked: false, hideTooltips: false, timerDuration: 0, isTimerDisabled: true }, backgroundColor: '#FF7A00' },
  { id: 4, name: 'spritz', slug: 'spritz', image: trainer4, path: 'spritz', defaultConfig: { speed: 100, isTimerDisabled: true }, backgroundColor: '#FFDF39' },
  {
    id: 5,
    name: 'mentalArithmetic',
    slug: 'mentalna-aryfmetyka',
    image: trainer5,
    path: 'speed-math',
    defaultConfig: { formula: "DIRECT_COUNT", selectedNumbers: "4", digitNumber: "1", actions: 2, correctAnswersGoal: 10, speed: 0.5, isSoundEnabled: false, isTimerDisabled: true },
    backgroundColor: '#0066FF',
    coinConditions: {
      items: ['games.conditions.min_actions_10'],
      rules: {
        minActions: 10,
        requiredWin: true
      }
    }
  },
  { id: 6, name: 'fillwords', slug: 'filvordy', image: trainer6, path: 'fillwords', defaultConfig: { size: 5, numberOfFields: 1, timerDuration: 0, isTimerDisabled: true }, backgroundColor: '#F47BFF' },
  { id: 7, name: 'stroopTest', slug: 'test-strupa', image: trainer7, path: 'stroop-test', defaultConfig: { mode: "WORDS", words: 50, speed: 0.5, isSoundEnabled: true, isTimerDisabled: true }, backgroundColor: '#0066FF' },
  { id: 8, name: 'alphabet', slug: 'alfavit', image: trainer8, path: 'alphabet', defaultConfig: { mode: "WORDS", words: 5, speed: 1, tableSize: 3, handsAndLegs: false, isTimerDisabled: true }, backgroundColor: '#FFDF39' },
  { id: 9, name: 'findTheWord', slug: 'znaidy-slovo', image: trainer9, path: 'find-the-word', defaultConfig: { mode: "FIND_LETTER", size: 3, numberOfTables: 1, isTimerDisabled: true }, backgroundColor: '#42D60E' },
  { id: 10, name: 'texts', slug: 'teksty', image: trainer10, path: 'texts', defaultConfig: { mode: "NO_CHANGES", speed: 1, frequency: 20, height: 40, offset: 0, LETTER_REPLACEMENTS: [], isTimerDisabled: true }, backgroundColor: '#FF7B7B' },
  { id: 11, name: 'cybercat', slug: 'kiberkishka', image: trainer11, path: 'dino', defaultConfig: { speed: 2, obstacles: 2, isSoundEnabled: true, isCoinsEnabled: false, isSpeedScaleEnabled: false, isTimerDisabled: true }, backgroundColor: '#FF7A00' },
  { id: 12, name: 'flashCards', slug: 'flesh-karty', image: trainer12, path: 'flash-cards', defaultConfig: { rods: 1, numberLimit: 9, directions: "NUMBER_ABACUS", mode: "TRAINING", speed: 1, impulses: 1, correctAnswersGoal: 20, isTimerDisabled: true }, backgroundColor: '#0066FF' },
  { id: 13, name: 'abacus', slug: 'abakus', image: trainer13, path: 'abacus', defaultConfig: { rods: 1, isTimerDisabled: true }, backgroundColor: '#FFDF39' },
  {
    id: 14,
    name: 'findTheCybercat',
    slug: 'znaidy-kiberkishku',
    image: trainer14,
    path: 'find-the-cyber-cat',
    defaultConfig: { size: 3, steps: 3, speed: 0.5, isSoundEnabled: true, isTimerDisabled: true },
    backgroundColor: '#42D60E',
    coinConditions: {
      items: ['games.conditions.min_steps_15'],
      rules: {
        minSteps: 15,
        requiredWin: true
      }
    }
  },
  {
    id: 15,
    name: 'mnemonics',
    slug: 'mnemotekhnika',
    image: trainer15,
    path: 'mnemonics',
    defaultConfig: { size: 3, mode: true, picturesEnabled: true, wordsEnabled: false, numbersEnabled: false, colorsEnabled: false, timerDuration: 0, digitNumber: 2, isTimerDisabled: true },
    backgroundColor: '#0066FF',
    coinConditions: {
      items: ['games.conditions.field_5x5', 'games.conditions.zero_mistakes'],
      rules: {
        minFieldSize: 5,
        maxMistakes: 0,
        requiredWin: true
      }
    }
  },
  {
    id: 16,
    name: 'multiplicationDivision',
    slug: 'mnozhennia-and-dilennia',
    image: trainer16,
    path: 'multiplication-division',
    defaultConfig: { firstMultiplier: 1, firstMultiplierFixed: "1-9", secondMultiplier: 1, correctAnswers: 5, isTimerDisabled: true, mode: "multiply" },
    backgroundColor: '#42D60E',
    coinConditions: {
      items: ['games.conditions.field_5x5', 'games.conditions.zero_mistakes'],
      rules: {
        maxMistakes: 0,
        requiredWin: true
      }
    }
  },
  {
    id: 17,
    name: 'columns',
    slug: 'columns',
    image: trainer17,
    path: 'columns',
    defaultConfig: { formula: "DIRECT_COUNT", selectedNumbers: "4", term_size: "1", actions_amount: 10, correctAnswersGoal: 10, isTimerDisabled: true },
    backgroundColor: '#F47BFF'
  },
  {
    id: 18,
    name: 'multiplicationTricks',
    slug: 'mnozhennia-khytroshchi',
    image: trainer18,
    path: 'multiplication-tricks',
    defaultConfig: { isTimerDisabled: true },
    backgroundColor: '#FF7B7B'
  },
];

const isLoadingIframe = ref(false);
const showCoinNotification = ref(true);
const route = useRoute();
const router = useRouter();
const homeworksStore = useHomeworksStore();
const { locale, t } = useI18n();

const iframeRef = ref<HTMLIFrameElement | null>(null);
const iframeViewRef = ref<HTMLElement | null>(null);
const { isFullscreen, toggle } = useFullscreen(iframeViewRef);

const selectedTrainer = computed(() => {
  const slug = route.params.trainerSlug;
  if (!slug) return null;
  return trainers.find(t => t.slug === slug) || null;
});

const configuredIframeUrl = computed(() => {
  if (!selectedTrainer.value) return '';

  // Примечание: В интерфейсе Trainer iframeUrl был опциональным,
  // здесь мы проверяем path, так как он используется для генерации URL
  if (!selectedTrainer.value.path) {
    console.error('Path is missing for trainer:', selectedTrainer.value.name);
    return '';
  }

  const baseUri = import.meta.env.VITE_GAMES_BASE_URI;
  const cleanBaseUri = baseUri.replace(/\/+$/, '');
  const baseUrl = `${cleanBaseUri}/${selectedTrainer.value.path}/`;

  const searchParams = new URLSearchParams();

  // 1. Начинаем с дефолтного конфига тренажера
  let finalConfig = { ...selectedTrainer.value.defaultConfig };

  // 2. Если в URL есть параметр config (переданный из домашнего задания),
  // парсим его и объединяем с дефолтным, перезаписывая совпадающие ключи.
  if (route.query.config) {
    try {
      const queryConfig = typeof route.query.config === 'string'
          ? JSON.parse(route.query.config as string)
          : route.query.config;

      finalConfig = { ...finalConfig, ...queryConfig };
    } catch (e) {
      console.error('[GamesPage] Error parsing config from URL:', e);
    }
  }

  // 3. Устанавливаем итоговый конфиг как строку
  searchParams.set('config', JSON.stringify(finalConfig));

  searchParams.set('lang', locale.value);

  const isHomeworkContext = !!route.query.homeworkId;
  searchParams.set('isStudentCabinet', isHomeworkContext ? 'true' : 'false');

  // 4. Добавляем остальные параметры из query, исключая 'config',
  // так как мы его уже обработали выше.
  const queryParams = route.query;
  for (const key in queryParams) {
    if (key !== 'config' && queryParams[key] !== null && queryParams[key] !== undefined) {
      searchParams.append(key, queryParams[key] as string);
    }
  }

  return `${baseUrl}?${searchParams.toString()}`;
});

const selectTrainer = (trainer: Trainer) => {
  router.push({ name: 'game-view', params: { trainerSlug: trainer.slug } });
};

const goBackToGrid = () => {
  router.push({ name: 'games-grid' });
};

const getTargetOrigin = () => {
  try {
    const url = new URL(import.meta.env.VITE_GAMES_BASE_URI);
    return url.origin;
  } catch {
    return '*';
  }
};

const sendLangToIframe = (lang: string) => {
  if (iframeRef.value && iframeRef.value.contentWindow && selectedTrainer.value) {
    const targetOrigin = getTargetOrigin();
    console.log('[GamesPage] Sending language:', lang, 'to origin:', targetOrigin);
    try {
      iframeRef.value.contentWindow.postMessage(
          { type: 'setLanguage', lang: lang },
          targetOrigin
      );
    } catch {
      iframeRef.value.contentWindow.postMessage(
          { type: 'setLanguage', lang: lang },
          '*'
      );
    }
  }
};

const handleIframeLoad = () => {
  isLoadingIframe.value = false;
  sendLangToIframe(locale.value);
};

watch(locale, (newLang) => {
  sendLangToIframe(newLang);
});

const awardCoins = async (trainerName: string) => {
  console.log(`[COINS] 🚀 Starting API Request for ${trainerName}...`);
  try {
    const response = await authApi.post('/v1/student/me/game-reward', {
      amount: 10,
      comment: `Reward for perfect game in ${trainerName}`
    });

    console.log('[COINS] ✅ Success! API Response:', response);
    showCoinNotification.value = false;
  } catch (error: any) {
    console.error('[COINS] ❌ API Request Failed:', error);
  }
};

const handleGameEvent = async (event: MessageEvent) => {
  const data = event.data;

  if (typeof data !== 'object' || data === null) {
    return;
  }

  if (data.type === 'GAME_APP_READY') {
    console.log('[GamesPage] Received GAME_APP_READY. Resending language...');
    isLoadingIframe.value = false;
    sendLangToIframe(locale.value);
    return;
  }

  if (data.type !== 'gamePlayed' || !data.payload) {
    return;
  }

  console.log('[GAME_EVENT] 🎯 Valid game payload found:', data.payload);

  if (data.payload.isWin === true) {
    const activeId = homeworksStore.getActiveHomeworkId;
    if (activeId) {
      try {
        await homeworksStore.markHomeworkAsDone(activeId);
      } catch (error) {
        console.error("Error while marking homework as done:", error);
      }
    }
  }

  if (!selectedTrainer.value) return;

  const trainerConditions = selectedTrainer.value.coinConditions?.rules;

  if (trainerConditions) {
    const { mistakes, isWin } = data.payload;
    let { actions, steps, fieldSize } = data.payload;

    const configKey = Object.keys(data.payload).find(key => key.endsWith(':Config'));
    const config = configKey ? data.payload[configKey] : null;

    if (config) {
      console.log('[COINS] 🔧 Found config object in payload:', config);

      if (fieldSize === undefined && config.size !== undefined) {
        fieldSize = config.size;
      }

      if (steps === undefined && config.steps !== undefined) {
        steps = config.steps;
      }

      if (actions === undefined) {
        if (config.actions !== undefined) actions = config.actions;
        else if (config.count !== undefined) actions = config.count;
      }
    }

    let isEligible = true;

    console.log(`[COINS] Stats -> Mistakes: ${mistakes}, Win: ${isWin}, Actions: ${actions}, Steps: ${steps}, Field: ${fieldSize}`);

    if (trainerConditions.requiredWin && isWin !== true) {
      console.log('[COINS] ⛔ Failed: Win required');
      isEligible = false;
    }

    if (trainerConditions.maxMistakes !== undefined) {
      const mistakesCount = mistakes ?? 0;
      if (mistakesCount > trainerConditions.maxMistakes) {
        console.log(`[COINS] ⛔ Failed: Too many mistakes (${mistakesCount} > ${trainerConditions.maxMistakes})`);
        isEligible = false;
      }
    }

    if (trainerConditions.minActions !== undefined) {
      const actionsCount = actions ?? data.payload.count ?? data.payload.answers ?? 0;

      if (actionsCount < trainerConditions.minActions) {
        console.log(`[COINS] ⛔ Failed: Not enough actions (${actionsCount} < ${trainerConditions.minActions})`);
        isEligible = false;
      }
    }

    if (trainerConditions.minSteps !== undefined) {
      const stepsCount = steps || 0;
      if (stepsCount < trainerConditions.minSteps) {
        console.log(`[COINS] ⛔ Failed: Not enough steps (${stepsCount} < ${trainerConditions.minSteps})`);
        isEligible = false;
      }
    }

    if (trainerConditions.minFieldSize !== undefined) {
      if (fieldSize === undefined) {
        console.log('[COINS] ⛔ Failed: Field size check required but fieldSize is undefined');
        isEligible = false;
      } else if (fieldSize < trainerConditions.minFieldSize) {
        console.log(`[COINS] ⛔ Failed: Field too small (${fieldSize} < ${trainerConditions.minFieldSize})`);
        isEligible = false;
      }
    }

    if (isEligible) {
      console.log('[COINS] 🎉 CONDITIONS MET! Calling awardCoins...');
      await awardCoins(selectedTrainer.value.name);
    } else {
      console.log('[COINS] 💨 Conditions not met.');
    }
  }
};

watch(
    configuredIframeUrl,
    (newUrl) => {
      if (newUrl) {
        isLoadingIframe.value = true;
        showCoinNotification.value = true;
      }
    },
    { immediate: true }
);

watch(locale, (newLang) => {
  console.log(`[GamesPage] Locale watcher triggered: ${newLang}`);
  sendLangToIframe(newLang);
});

watch(
    () => route.query.homeworkId,
    (newId) => {
      const homeworkId = newId ? parseInt(newId as string, 10) : null;
      homeworksStore.setActiveHomework(homeworkId);
    },
    {
      immediate: true,
    }
);

onMounted(() => {
  window.addEventListener('message', handleGameEvent);
});

onUnmounted(() => {
  window.removeEventListener('message', handleGameEvent);
});
</script>

<template>
  <div class="games-page">
    <div v-if="selectedTrainer" ref="iframeViewRef" class="iframe-view" :class="{ 'iframe-view--fullscreen': isFullscreen }">
      <div v-if="!isFullscreen" class="iframe-header">
        <span class="back-arrow" @click="goBackToGrid">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 10H5M5 10L10 5M5 10L10 15" stroke="#FFFFFF" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <h1 class="iframe-title">{{ $t(selectedTrainer.name) }}</h1>
        <button class="fullscreen-btn" @click="toggle">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 3H21V9M21 3L14 10M9 21H3V15M3 21L10 14" stroke="#FFFFFF" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
      <div class="iframe-container">
        <div v-if="isLoadingIframe" class="spinner-overlay">
          <div class="spinner"></div>
        </div>
        <iframe
            ref="iframeRef"
            :src="configuredIframeUrl"
            class="trainer-iframe"
            frameborder="0"
            allowfullscreen
            :class="{ 'iframe-hidden': isLoadingIframe }"
            @load="handleIframeLoad"
        ></iframe>

        <div
            v-if="selectedTrainer?.coinConditions && showCoinNotification && !isLoadingIframe"
            class="coin-notification"
        >
          <div class="coin-icon">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M26.9073 13.5834C29.2965 13.5834 31.2948 15.5259 31.0784 17.9052C30.758 21.4281 29.8025 24.7328 27.0371 27.4697C24.2685 30.2094 20.6635 31.5793 16.2223 31.5793C11.4926 31.5793 7.59922 30.0652 4.54223 27.0371C1.51408 24.0089 0 20.2742 0 15.8329C0 11.3916 1.51408 7.64248 4.54223 4.58549C7.57038 1.5285 11.3195 0 15.7897 0C19.6949 0 23.6062 1.38674 26.5125 3.96235C28.1274 5.39352 27.5086 7.85862 25.6358 8.93054C23.8778 9.93677 21.6106 9.2742 19.7784 8.41026C18.565 7.83808 17.1881 7.57039 15.8762 7.57039C13.4825 7.57039 11.5214 8.34905 9.99291 9.90639C8.46441 11.4637 7.70016 13.4537 7.70016 15.8762C7.70016 18.2122 8.44999 20.1877 9.94965 21.8027C11.4781 23.4177 13.6123 24.2252 16.352 24.2252C19.8705 24.2252 22.192 22.8986 23.3168 20.2454H19.2937C17.454 20.2454 15.9627 18.754 15.9627 16.9144C15.9627 15.0748 17.454 13.5834 19.2937 13.5834H26.9073Z" fill="#EB6200"/>
            </svg>
          </div>

          <div class="coin-content">
            <div class="coin-title">{{ $t('games.conditions.title') }}</div>
            <div class="coin-rules">
              <div v-for="(rule, index) in selectedTrainer.coinConditions.items" :key="index" class="coin-rule-item">
                {{ $t(rule) }}
              </div>
            </div>
          </div>

          <button class="coin-close-btn" @click="showCoinNotification = false">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="24" height="24" rx="8" fill="#0066FF"/>
              <path d="M16 8L8 16M8 8L16 16" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <div v-else>
      <div class="games-grid">
        <div
            v-for="trainer in trainers"
            :key="trainer.id"
            :class="['game-card', { 'small-image': trainer.id === 18 }]"
            :style="{ backgroundColor: trainer.backgroundColor || '#FFFFFF' }"
            @click="selectTrainer(trainer)"
        >
          <img :src="trainer.image" :alt="$t(trainer.name)" class="game-image" />
          <span class="game-name">{{ $t(trainer.name) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import '@/assets/scss/mixins/mixins-media.scss';

.games-page {
  /*PROBABLY FOR MOBILE DEVICES*/
  //padding-bottom: 50px;
}

.page-title {
  font-weight: 500;
  font-size: 32px;
  color: #333;
  margin-bottom: 24px;
}

.games-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

@include media-max(mobile) {
  .games-grid {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }
  .game-card {
    min-width: unset;
  }
}

@include media-between(tablet, desktop) {
  .games-grid {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  }
}

.game-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 32px;
  padding: 26px;
  cursor: pointer;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  height: 284px;
  position: relative;

  img {
    width: 273px;
    height: 180px;
  }
}

.game-card.small-image {
  img {
    height: 150px;
  }
}

.game-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.game-image {
  object-fit: contain;
  margin-bottom: 12px;
}

.game-name {
  font-weight: 500;
  font-size: 16px;
  color: #333;
  position: absolute;
  left: 32px;
  bottom: 36px;
  font-weight: 500;
  font-size: 26px;
  line-height: 105%;
  letter-spacing: 0%;
  color: #FFFFFF;
}

.iframe-view {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 120px); /* page bottom and top padding */
  //min-height: 1200px;
}

.iframe-view--fullscreen {
  height: 100vh;
  background: #000;
}

.iframe-view--fullscreen .iframe-container {
  border-radius: 0;
  padding: 0;
  background-color: #000;
  flex-grow: 1;
}

.iframe-view--fullscreen .trainer-iframe {
  border-radius: 0;
}

.iframe-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.back-arrow {
  cursor: pointer;
  display: flex;
  align-items: center;
}

.fullscreen-btn {
  margin-left: auto;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  padding: 7px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.fullscreen-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
}

.iframe-title {
  font-weight: bolder;
  font-size: 32px;
  color: #ffffff;
}

.iframe-container {
  position: relative;
  flex-grow: 1;
  width: 100%;
  border-radius: 32px;
  overflow: hidden;
  padding: 25px;
  background-color: #eff3f4;
}

.trainer-iframe {
  width: 100%;
  height: 100%;
  border-radius: 8px;
}

.iframe-hidden {
  visibility: hidden;
}

.spinner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  border-radius: 8px;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.coin-notification {
  position: absolute;
  bottom: 40px;
  right: 40px;
  background: #FFFFFF;
  box-shadow: 0px 10px 40px rgba(0, 0, 0, 0.1);
  border-radius: 24px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  z-index: 5;
  animation: slideIn 0.5s ease-out;
  max-width: 456px;
  border: 2px solid #0066FF
}

.coin-icon {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  background: #FFD139;
  border-radius: 55px;
  display: flex;
  justify-content: center;
  align-content: center;
  align-items: center;
}

.coin-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.coin-title {
  font-weight: 400;
  font-size: 18px;
  line-height: 25px;
  color: #828282;
}

.coin-rules {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.coin-rule-item {
  font-weight: 600;
  font-size: 32px;
  color: #0066FF;
}

.coin-close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-left: 12px;
  flex-shrink: 0;
  transition: opacity 0.2s;
}

.coin-close-btn:hover {
  opacity: 0.8;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@include media-max(mobile) {
  .games-grid {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }
  .game-card {
    min-width: unset;
  }
  .coin-notification {
    bottom: 20px;
    right: 20px;
    left: 20px;
    width: auto;
    padding: 12px 16px;
  }
  .coin-rule-item {
    font-size: 14px;
  }
}
</style>